import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, TextField, CheckboxField, Button, Alert, Spinner } from '@dopomogai/ui';
import type { InvestCreateResponse, InvestmentOfferingConfig, InvestmentStats } from '@dopomogai/types';
import { useInvestInAgent, useUserPortfolio, useAnalytics } from '@dopomogai/agent-kit';

type Props = {
  agentId: string;
  offering: InvestmentOfferingConfig;
  stats: InvestmentStats;
  termsVersion: string;
  onSuccess?: (data: InvestCreateResponse) => void;
  unauthenticated?: boolean;
  initialIdempotencyKey?: string | null;
  onRequireAuth?: () => void;
  returnPath?: string;
  // --- NEW PROPS FOR LIFTED STATE & UX ---
  shares: number;
  onSharesChange: (shares: number) => void;
  userCreditBalance?: number;
  isBalanceLoading?: boolean;
  onTopUpRequested?: (intent: { agent_id: string; shares_to_buy: number; idempotency_key: string | null; terms_version: string; return_path?: string }) => void;
};

export function InvestmentForm({
  agentId,
  offering,
  stats,
  termsVersion,
  onSuccess,
  unauthenticated = false,
  initialIdempotencyKey,
  onRequireAuth,
  returnPath,
  // --- NEW PROPS ---
  shares,
  onSharesChange,
  userCreditBalance,
  isBalanceLoading,
  onTopUpRequested,
}: Props) {
  const analytics = useAnalytics();
  const { invest, isLoading, error, data, resetAttempt } = useInvestInAgent();
  const { mutate: mutatePortfolio } = useUserPortfolio();

  const [accepted, setAccepted] = useState(false);
  const idempoRef = useRef<string | null>(null);

  useEffect(() => {
    if (initialIdempotencyKey) {
      idempoRef.current = initialIdempotencyKey;
    }
  }, [initialIdempotencyKey]);

  const totalCost = useMemo(() => shares * offering.price_per_share_credits, [shares, offering.price_per_share_credits]);
  const hasSufficientFunds = userCreditBalance !== undefined && userCreditBalance >= totalCost;

  const canSubmit = offering.status === 'active' && accepted && shares >= offering.min_investment_shares && (!offering.max_investment_shares || shares <= offering.max_investment_shares) && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (unauthenticated) {
      const key = idempoRef.current || (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : null);
      idempoRef.current = key;
      const intent = { agent_id: agentId, shares_to_buy: shares, idempotency_key: key, terms_version: termsVersion, return_path: returnPath || `/invest/${agentId}` };
      try { sessionStorage.setItem('dopomogai_invest_intent', JSON.stringify(intent)); } catch {}
      onRequireAuth && onRequireAuth();
      return;
    }
    analytics.track('invest_submit_click', { agent_id: agentId, shares: shares });
    const res = await invest({ agent_id: agentId, shares_to_buy: shares, terms_version: termsVersion, idempotency_key: idempoRef.current || undefined });
    if (res?.success) {
      mutatePortfolio();
      onSuccess?.(res);
    }
  };

  return (
    <Card>
      <div className="space-y-4 p-4">
        {data?.success && (
          <Alert type="success" title="Purchase complete" message={`You bought ${data.shares_purchased} shares. New balance: ${data.new_balance_credits} cr.`} />
        )}
        {error && (
          <Alert type="error" title="Purchase failed" message={String(error.message || 'Could not complete purchase')} />
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="border-2 border-black rounded-lg p-3 bg-white">
            <div className="text-xs text-slate-600">Price per share</div>
            <div className="text-lg font-extrabold">{offering.price_per_share_credits} cr</div>
          </div>
          <div className="border-2 border-black rounded-lg p-3 bg-white">
            <div className="text-xs text-slate-600">Remaining</div>
            <div className="text-lg font-extrabold">{Math.max(0, offering.total_shares - stats.sold_shares)}</div>
          </div>
          <div className="border-2 border-black rounded-lg p-3 bg-white">
            <div className="text-xs text-slate-600">Round size</div>
            <div className="text-lg font-extrabold">{offering.total_shares}</div>
          </div>
        </div>

        {/* --- UX IMPROVEMENT 3: SHOW USER BALANCE --- */}
        {!unauthenticated && (
          <div className="p-3 bg-slate-50 border-2 border-black rounded-lg text-sm flex items-center justify-between">
            <span className="text-slate-700">Your balance</span>
            {isBalanceLoading ? (
              <Spinner size="sm" />
            ) : (
              <span className="font-bold">{userCreditBalance?.toLocaleString() ?? 0} cr</span>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Shares to buy"
            type="number"
            value={String(shares)}
            onChange={(e) => {
              const v = parseInt(e.target.value || '0', 10);
              onSharesChange(Number.isNaN(v) ? 0 : v); // Use callback to lift state
              analytics.track('invest_shares_change', { agent_id: agentId, shares: v });
            }}
          />
          <div className="border-2 border-black rounded-lg p-3 bg-white">
            <div className="text-xs text-slate-600">Total cost</div>
            <div className="text-lg font-extrabold">{totalCost.toLocaleString()} cr</div>
          </div>
        </div>

        <div className="text-xs text-slate-600">Min {offering.min_investment_shares} • {offering.max_investment_shares || '—'} max per transaction</div>

        {/* UX: Show warning if funds are insufficient */}
        {!unauthenticated && !isBalanceLoading && !hasSufficientFunds && (
            <Alert type="warning" title="Insufficient Credits" message={`You need ${totalCost.toLocaleString()} cr to make this purchase.`} />
        )}

        <CheckboxField
          checked={accepted}
          onChange={(c) => {
            setAccepted(c);
            analytics.track('invest_terms_toggle', { agent_id: agentId, accepted: c });
          }}
          label={`I accept Terms v${termsVersion}`}
        />

        <div className="flex gap-3">
          <Button
            text="Top up balance"
            variant="secondary"
            onClick={() => {
              analytics.track('invest_topup_click', { agent_id: agentId });
              const key = idempoRef.current || (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : null);
              idempoRef.current = key;
              const intent = { agent_id: agentId, shares_to_buy: shares, idempotency_key: key, terms_version: termsVersion, return_path: returnPath || `/invest/${agentId}` };
              try { sessionStorage.setItem('dopomogai_invest_intent', JSON.stringify(intent)); } catch {}
              // Redirect to the top-up flow or auth, as decided by the parent
              if (onTopUpRequested) {
                onTopUpRequested(intent);
              } else {
                // Safe fallback: go to buy-credits if available.
                if (typeof window !== 'undefined') {
                  window.location.href = '/app/buy-credits';
                }
              }
            }}
          />
          <Button text="Confirm purchase" onClick={handleSubmit} disabled={!canSubmit || (!unauthenticated && !hasSufficientFunds)} />
        </div>

        {offering.status !== 'active' && <div className="text-sm text-rose-600">Funding not active.</div>}
      </div>
    </Card>
  );
}


